import { useEffect, useState } from 'react';

interface DockviewDisposable {
  dispose: () => void;
}

interface DockviewLocationApi {
  location: DockviewLocation;
  onDidLocationChange: (listener: () => void) => DockviewDisposable;
}

type DockviewLocation = { type: string } | DockviewPopoutLocation;

interface DockviewPopoutLocation {
  type: 'popout';
  getWindow: () => Window;
}

/**
 * Tracks the popout window for a Dockview API object.
 *
 * Dockview mutates `api.location` when a panel/group moves between the main
 * grid and a popout window. That mutation is outside React, so reading
 * `api.location` during render is not enough: React will not re-render unless
 * we subscribe to `onDidLocationChange` and store the current popout window in
 * state.
 *
 * Components use this to choose the correct document for popovers/portals and
 * to target styled-components at the popout document when Dockview renders tab
 * or header chrome outside the panel body.
 */
export function useDockviewPopoutWindow(api: DockviewLocationApi) {
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(() => getDockviewPopoutWindow(api));

  useEffect(() => {
    const updatePopoutWindow = () => {
      setPopoutWindow(getDockviewPopoutWindow(api));
    };

    // Catch any location change that happened between render and effect setup.
    updatePopoutWindow();

    const disposable = api.onDidLocationChange(updatePopoutWindow);
    return () => disposable.dispose();
  }, [api]);

  return popoutWindow;
}

function getDockviewPopoutWindow(api: DockviewLocationApi) {
  return isDockviewPopoutLocation(api.location) ? api.location.getWindow() : null;
}

function isDockviewPopoutLocation(location: DockviewLocation): location is DockviewPopoutLocation {
  return location.type === 'popout';
}
